import { SimplePagination } from '@features/ui-atoms/components/Pagination/SimplePagination';
import {
  Table,
  TableBody,
  TableRow,
  TableSkeletonCell,
  TableSkeletonRow,
} from '@features/ui-atoms/components/TableV2/Table';
import { Skeleton } from '@mui/joy';
import { getSstColumns } from '@root/src/entities/Sst/columns';
import { SstFront } from '@root/src/entities/Sst/type';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { useState } from 'react';

interface SstTableProps {
  data: SstFront[];
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  skeletonRows?: number;
  onRowClick?: (sst: SstFront) => void;
}

export function SstTable({
  data,
  currentPage,
  pageCount,
  onPageChange,
  isLoading = false,
  skeletonRows = 8,
  onRowClick,
}: SstTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable<SstFront>({
    data,
    columns: getSstColumns(),
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 15,
        pageIndex: currentPage - 1,
      },
    },
    manualPagination: true,
    pageCount,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', width: '100%' }}>
      <Table>
        <TableBody>
          {isLoading
            ? Array.from({ length: skeletonRows }).map(() => (
                <TableSkeletonRow key={crypto.randomUUID()}>
                  <TableSkeletonCell colSpan={1}>
                    <Skeleton
                      variant="rectangular"
                      height={62}
                      sx={{
                        padding: 0,
                        backgroundColor: 'transparent',
                        '--unstable_pulse-bg': 'rgba(255, 255, 255, 0.5)',
                        '--unstable_wave-bg': 'rgba(255, 255, 255, 0.8)',
                        '--joy-palette-background-level3': 'rgba(255, 255, 255, 1)',
                      }}
                    />
                  </TableSkeletonCell>
                </TableSkeletonRow>
              ))
            : table.getRowModel().rows.map(row => (
                <TableRow key={row.id} onClick={() => onRowClick?.(row.original)} style={{ cursor: 'pointer' }}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} style={{ padding: 0 }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </TableRow>
              ))}
        </TableBody>
      </Table>

      {pageCount > 1 && (
        <SimplePagination currentPage={currentPage} pageCount={pageCount} onPageChange={onPageChange} />
      )}
    </div>
  );
}
